import { perf } from "./perf";

const rand = (a, b) => a + Math.random() * (b - a);
const backOut = (x) => 1 + 2.2 * Math.pow(x - 1, 3) + 1.2 * Math.pow(x - 1, 2);
const EXT = 1.75;
const POP = 0.9;
const FADE = 2.2;

const VERT = `attribute vec2 aCorner;
attribute vec4 aBub;
attribute vec4 aState;
uniform vec2 uView;
varying vec2 vLocal;
varying float vSeed;
varying vec4 vState;
void main(){
  float ext = aState.y > 0.0 ? ${EXT.toFixed(2)} : 1.04;
  vec2 p = aBub.xy + aCorner * aBub.z * ext;
  vLocal = aCorner * ext;
  vSeed = aBub.w;
  vState = aState;
  gl_Position = vec4(p.x / uView.x * 2.0 - 1.0, 1.0 - p.y / uView.y * 2.0, 0.0, 1.0);
}`;

const HASH = "float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }";

const FILM = `precision mediump float;
uniform float uTime;
varying vec2 vLocal;
varying float vSeed;
varying vec4 vState;
void main(){
  float d = length(vLocal);
  float pop = vState.y;
  float foam = vState.w;
  if (pop <= 0.0) {
    if (d > 1.03) discard;
    float ang = atan(vLocal.y, vLocal.x);
    float s = 0.5 + 0.5 * sin(ang * 2.0 + uTime * 0.25 + vSeed * 6.28);
    vec3 pearl = vec3(0.985, 0.975, 0.99);
    vec3 whisper = mix(vec3(0.86, 0.82, 0.95), vec3(0.96, 0.84, 0.9), s);
    float rimz = smoothstep(0.62, 0.96, d) * (1.0 - smoothstep(0.96, 1.03, d));
    vec3 c = mix(pearl, whisper, rimz * 0.55 * (1.0 - foam * 0.7));
    float frost = 0.13 + 0.14 * smoothstep(0.2, 1.0, d);
    float a = frost * (1.0 - smoothstep(0.9, 1.03, d)) + rimz * 0.34;
    float hl = smoothstep(0.45, 0.0, length(vLocal - vec2(-0.35, -0.4))) * 0.12;
    a = (a + hl) * mix(1.0, 1.25, foam) * vState.x * (1.0 - vState.z);
    gl_FragColor = vec4(min(c + hl, 1.0) * a, a);
  } else {
    float R = 1.0 + pop * 0.5;
    float mist = exp(-pow((d - R * 0.8) / (0.25 + pop * 0.3), 2.0)) * (1.0 - pop) * (1.0 - pop);
    float a = mist * 0.22 * vState.x;
    gl_FragColor = vec4(vec3(0.98, 0.97, 1.0) * a, a);
  }
}`;

const QUAD = "attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }";
const WASH = `precision mediump float;
uniform vec2 uRes;
uniform float uTime;
${HASH}
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p){
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.03 + 1.7; a *= 0.5; }
  return v;
}
void main(){
  vec2 uv = gl_FragCoord.xy / uRes;
  vec2 p = uv * vec2(uRes.x / uRes.y, 1.0) * 2.2;
  float t = uTime * 0.035;
  vec2 q = vec2(fbm(p + vec2(0.0, t)), fbm(p + vec2(5.2, -t * 0.8)));
  float n = fbm(p + 1.6 * q + vec2(t * 0.5, t * 0.3));
  vec3 cream = vec3(0.98, 0.955, 0.95);
  vec3 c = mix(cream, vec3(0.773, 0.702, 0.843), smoothstep(0.28, 0.72, n));
  c = mix(c, vec3(0.91, 0.776, 0.847), smoothstep(0.38, 0.78, q.x) * 0.8);
  c = mix(c, vec3(0.80, 0.88, 0.96), smoothstep(0.55, 0.85, q.y) * 0.6);
  c = mix(c, vec3(0.96, 0.87, 0.70), smoothstep(0.62, 0.9, fbm(p * 0.7 - t)) * 0.5);
  c = mix(c, vec3(0.70, 0.56, 0.78), (1.0 - smoothstep(0.0, 0.03, abs(n - 0.5))) * 0.12 + (1.0 - smoothstep(0.0, 0.025, abs(q.x - 0.56))) * 0.08);
  vec2 cuv = (uv - 0.5) * vec2(1.5, 1.0);
  c = mix(c, cream, 0.32 * (1.0 - smoothstep(0.05, 0.55, length(cuv))));
  c *= 0.97 + 0.03 * noise(gl_FragCoord.xy * 0.35) + (hash(gl_FragCoord.xy) - 0.5) * 0.02;
  c = mix(c, cream, 0.4);
  gl_FragColor = vec4(c, 1.0);
}`;

function program(gl, vs, fs) {
  const sh = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.warn("soap shader", gl.getShaderInfoLog(s));
    return s;
  };
  const p = gl.createProgram();
  gl.attachShader(p, sh(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
}

export function createSoapScene(canvas, { onPop }) {
  const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
  if (!gl) return null;
  const inst = gl.getExtension("ANGLE_instanced_arrays");
  if (!inst) return null;
  const pFilm = program(gl, VERT, FILM);
  const pWash = program(gl, QUAD, WASH);
  if (!pFilm || !pWash) return null;

  let W = 0, H = 0, scale = 1, k = 1, raf = 0, last = performance.now(), t = 0, lowT = 0, lastPop = 0;
  const stats = { born: 0, popped: 0, faded: 0, touched: 0 };
  let pool = [], limit = 0, clusters = [], avoid = [];
  const wind = { x: 0, y: 0, s: 0 };
  stats.blown = 0;
  const MAXN = 160;
  const BUB = new Float32Array(MAXN * 4);
  const ST = new Float32Array(MAXN * 4);
  const mk = (data, usage) => {
    const b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, data, usage);
    return b;
  };
  const corner = mk(new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const tri = mk(new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const bubBuf = mk(BUB.byteLength, gl.DYNAMIC_DRAW);
  const stBuf = mk(ST.byteLength, gl.DYNAMIC_DRAW);

  const near = (c, spread) => [c.x + (Math.random() + Math.random() - 1) * spread, c.y + (Math.random() + Math.random() - 1) * spread * 0.5 - rand(0, 24)];
  const spawn = (b, initial) => {
    const c = clusters[Math.floor(Math.random() * clusters.length)];
    b.vx = 0;
    b.ph = rand(0, 6.28);
    b.seed = Math.random();
    b.phase = "wait";
    b.d = 0;
    b.r = 0;
    b.vis = 1;
    if (b.foam) {
      b.R = rand(3, 9) * Math.max(k, 0.8);
      [b.x, b.y] = near(c, 46);
      if (initial) b.y -= rand(0, H * 0.18);
      b.vy = -rand(4, 10);
      b.born = rand(0.8, 1.6);
      b.life = rand(4, 9);
      b.end = "fade";
      b.age = -rand(0, initial ? 5 : 2);
      return;
    }
    const roll = Math.random();
    b.R = (roll < 0.55 ? rand(12, 26) : roll < 0.9 ? rand(26, 46) : rand(46, 70)) * k;
    if (Math.random() < 0.3) [b.x, b.y] = near(c, 30);
    else {
      b.x = rand(0.04, 0.96) * W;
      b.y = initial ? rand(0.15, 1.0) * H : rand(0.5, 1.02) * H;
    }
    b.vy = -(5 + 140 / (b.R / k + 10));
    b.born = rand(1.4, 2.2);
    b.life = rand(8, 16);
    b.end = Math.random() < 0.25 ? "pop" : "fade";
    b.age = -rand(0.5, initial ? 8 : 3);
  };
  const makeClusters = () => {
    const n = W < 600 ? 4 : 6;
    clusters = Array.from({ length: n }, (_, i) => {
      if (i < Math.ceil(n / 2)) return { x: ((i + 0.5) / Math.ceil(n / 2)) * W + rand(-40, 40), y: H + 4 };
      const left = i % 2 === 0;
      return { x: left ? rand(-6, 20) : W - rand(-6, 20), y: rand(0.5, 0.95) * H };
    });
  };
  const kill = (b, how) => {
    if (b.phase !== "born" && b.phase !== "float") return false;
    b.phase = how;
    b.d = 0;
    return true;
  };

  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    k = W < 600 ? 0.68 : 1;
    scale = W > 1000 ? 0.6 : 0.85;
    canvas.width = Math.floor(W * scale);
    canvas.height = Math.floor(H * scale);
    makeClusters();
  }

  function hit(x, y) {
    for (let i = 0; i < limit; i++) {
      const b = pool[i];
      if ((b.phase === "float" || b.phase === "born") && Math.hypot(b.x - x, b.y - y) < Math.max(b.r, 14)) return b;
    }
    return null;
  }

  function attrib(prog, name, buf, size, div) {
    const l = gl.getAttribLocation(prog, name);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.enableVertexAttribArray(l);
    gl.vertexAttribPointer(l, size, gl.FLOAT, false, 0, 0);
    inst.vertexAttribDivisorANGLE(l, div);
    return l;
  }

  function update(dt) {
    wind.s *= Math.exp(-2.5 * dt);
    for (let i = 0; i < limit; i++) {
      const b = pool[i];
      b.age += dt;
      if (b.phase === "wait") {
        if (b.age >= 0) {
          b.phase = "born";
          stats.born++;
        } else continue;
      }
      if (wind.s > 0.02 && (b.phase === "float" || b.phase === "born")) {
        const dx = b.x - wind.x, dy = b.y - wind.y;
        const d = Math.hypot(dx, dy) + 1;
        const f = (wind.s * 1100) / (1 + d / 260);
        b.vx += (dx / d) * f * dt;
        b.wy = (b.wy || 0) + (dy / d) * f * dt;
        if (wind.s > 0.25 && Math.random() < wind.s * dt * 1.1 && kill(b, "fade")) stats.blown++;
        if ((b.x < -b.R || b.x > W + b.R || b.y > H + b.R) && kill(b, "fade")) stats.blown++;
      }
      if (b.wy) {
        b.y += b.wy * dt;
        b.wy *= 0.93;
      }
      if (b.phase === "pop" || b.phase === "fade") {
        b.d += dt / (b.phase === "pop" ? POP : FADE);
        if (b.d >= 1) {
          stats[b.phase === "pop" ? "popped" : "faded"]++;
          spawn(b, false);
        }
        if (b.phase === "fade") b.y += b.vy * dt * 0.6;
        continue;
      }
      if (b.phase === "born" && b.age >= b.born) b.phase = "float";
      if (b.phase === "float" && b.age >= b.born + b.life) kill(b, b.end);
      const grow = b.phase === "born" ? 0.15 + 0.85 * backOut(Math.min(1, b.age / b.born)) : 1;
      b.shim = (b.shim || 0) * Math.exp(-3 * dt);
      b.r = b.R * grow * (1 + 0.03 * Math.sin(t * 1.4 + b.ph) + b.shim * 0.07 * Math.sin(t * 16 + b.ph * 3));
      const lift = b.phase === "born" ? 0.25 : 1;
      let inside = null;
      for (const z of avoid) {
        const m = b.r + 18;
        if (b.x > z.left - m && b.x < z.right + m && b.y > z.top - m && b.y < z.bottom + m) {
          inside = z;
          break;
        }
      }
      b.vis += ((inside ? 0 : 1) - b.vis) * Math.min(1, dt * 1.5);
      if (inside) b.x += Math.sign(b.x - (inside.left + inside.right) / 2 || 1) * 26 * dt;
      b.x += (Math.sin(t * 0.35 + b.ph) * 7 + b.vx) * dt;
      b.y += b.vy * dt * lift;
      b.vx *= 0.92;
      if (b.y < -b.R * 1.2) kill(b, "fade");
    }
    for (let i = 0; i < limit; i++) {
      const a = pool[i];
      if (a.phase !== "float" && a.phase !== "born") continue;
      for (let j = i + 1; j < limit; j++) {
        const b = pool[j];
        if (b.phase !== "float" && b.phase !== "born") continue;
        const dx = b.x - a.x, dy = b.y - a.y;
        const min = (a.r + b.r) * 0.96;
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min || d2 < 0.01) continue;
        const d = Math.sqrt(d2);
        const push = ((min - d) / d) * 0.5;
        a.x -= dx * push;
        a.y -= dy * push * 0.5;
        b.x += dx * push;
        b.y += dy * push * 0.5;
      }
    }
  }

  function draw() {
    let n = 0;
    for (let i = 0; i < limit && n < MAXN; i++) {
      const b = pool[i];
      if (b.phase === "wait") continue;
      const alpha = (b.phase === "born" ? Math.min(1, (b.age / b.born) * 1.6) : 1) * (0.08 + 0.92 * (b.vis ?? 1));
      BUB.set([b.x, b.y, b.phase === "pop" ? b.R : b.r, b.seed], n * 4);
      ST.set([alpha, b.phase === "pop" ? Math.max(0.001, b.d) : 0, b.phase === "fade" ? b.d : 0, b.foam ? 1 : 0], n * 4);
      n++;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.disable(gl.BLEND);
    gl.useProgram(pWash);
    gl.uniform2f(gl.getUniformLocation(pWash, "uRes"), canvas.width, canvas.height);
    gl.uniform1f(gl.getUniformLocation(pWash, "uTime"), t);
    const lp = attrib(pWash, "p", tri, 2, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disableVertexAttribArray(lp);

    gl.bindBuffer(gl.ARRAY_BUFFER, bubBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, BUB.subarray(0, n * 4));
    gl.bindBuffer(gl.ARRAY_BUFFER, stBuf);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, ST.subarray(0, n * 4));
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(pFilm);
    gl.uniform2f(gl.getUniformLocation(pFilm, "uView"), W, H);
    gl.uniform1f(gl.getUniformLocation(pFilm, "uTime"), t);
    const locs = [attrib(pFilm, "aCorner", corner, 2, 0), attrib(pFilm, "aBub", bubBuf, 4, 1), attrib(pFilm, "aState", stBuf, 4, 1)];
    inst.drawArraysInstancedANGLE(gl.TRIANGLE_STRIP, 0, 4, n);
    locs.forEach((l) => {
      inst.vertexAttribDivisorANGLE(l, 0);
      gl.disableVertexAttribArray(l);
    });
  }

  function frame(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    t += dt;
    if (dt > 0) perf.fps += (Math.min(120, 1 / dt) - perf.fps) * 0.05;
    lowT = perf.fps < 30 ? lowT + dt : Math.max(0, lowT - dt);
    if (lowT > 2 && limit > 40) {
      limit = Math.max(40, Math.floor(limit * 0.8));
      lowT = 0;
    }
    update(dt);
    draw();
    raf = requestAnimationFrame(frame);
  }

  resize();
  const regular = W < 600 ? 35 : 60;
  limit = regular + (W < 600 ? 55 : 90);
  pool = Array.from({ length: limit }, (_, i) => {
    const b = { foam: i >= regular };
    spawn(b, true);
    return b;
  });
  window.addEventListener("resize", resize);
  raf = requestAnimationFrame(frame);

  const popAt = (x, y) => {
    const b = hit(x, y);
    if (!b || !kill(b, "pop")) return false;
    stats.touched++;
    onPop();
    return true;
  };

  return {
    mode: "soap",
    setAvoid(rects) {
      avoid = rects;
    },
    pointerMove(x, y) {
      const now = performance.now();
      if (now - lastPop > 140 && popAt(x, y)) lastPop = now;
    },
    pop: popAt,
    shimmer(x, y, a) {
      for (let i = 0; i < limit; i++) {
        const b = pool[i];
        if (b.phase !== "float" && b.phase !== "born") continue;
        const d = Math.hypot(b.x - x, b.y - y);
        if (d < 360) b.shim = Math.max(b.shim || 0, a * (1 - d / 360));
      }
    },
    blow(x, y, s) {
      wind.x = x;
      wind.y = y;
      wind.s = Math.max(wind.s, s);
    },
    info() {
      const live = pool.slice(0, limit);
      const phases = live.reduce((m, b) => ((m[b.phase] = (m[b.phase] || 0) + 1), m), {});
      return {
        mode: "soap",
        wind: +wind.s.toFixed(2),
        limit,
        regular: live.filter((b) => !b.foam).length,
        foam: live.filter((b) => b.foam).length,
        zones: avoid.length,
        inZone: live.filter((b) => b.phase !== "wait" && b.vis < 0.5).length,
        alive: live.filter((b) => b.phase !== "wait").length,
        phases,
        pops: stats.touched,
        ...stats,
        bubbles: live.filter((b) => b.phase === "float" || b.phase === "born").map((b) => ({ x: b.x, y: b.y, r: b.r, phase: b.phase })),
        fps: Math.round(perf.fps),
      };
    },
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    },
  };
}
